/**
 * The blackjack table's geometry: the half-moon plate, as a module.
 *
 * ============================== WHERE EVERY NUMBER IS FROM =======================
 * Two builders drew this table and both are ported here, neither re-imagined:
 *
 *   - `Poker assets/source/build-topdown.py` (mock td03, the frame switcher in
 *     `mockups/td03-topdown-2026-09-05.html`): `renderBJ()` — the felt's arc, the four
 *     spots 32 degrees apart, the printed legends on two arcs, the portrait zoom
 *     (`w = W * 1.55`, the plate anchored `0.13` of the stage under the top bar), the
 *     pads a landscape plate is fitted with, the YOU disc beside the hero's circle.
 *   - `Poker assets/source/build-blackjack.py` (mock bj07): the same arc (`CX, CY, R`
 *     are identical in both), the dealer standing behind the flat edge at its centre,
 *     the dealer's hand just below the tray at `0.215`, and the shoe at `(0.78, 0.21)`.
 *
 * Every position is a FRACTION OF THE PLATE — x of its width, y of its height — with the
 * dealer's straight edge at the top, as both mocks draw it. The plate art ships the
 * other way up (`blackjack-topdown-2200.webp` has its flat edge and brass tray at the
 * bottom), so the component turns the picture half a turn once, always: the half-moon
 * never rotates between orientations, which is the layout spec's rule.
 *
 * Numbers that are NOT in either builder are marked PHASE 8 with the reason, and the
 * report's geometry table lists each one, mock value -> shipped value.
 * ===============================================================================
 */

/** The plate's width ÷ height. `blackjack-topdown-2200.webp` is 2200 x 1238; the mocks' 1100 x 619. */
export const PLATE_ASPECT = 2200 / 1238;

/** The felt's semicircle: centre and radius (radius as a fraction of the WIDTH). Both builders: `CX, CY, R = 0.5, 0.14, 0.43`. */
export const CX = 0.5;
export const CY = 0.14;
export const R = 0.43;

/**
 * A point on the felt at `k` of the radius, at `deg` (90 = straight toward the player,
 * 0 = the player's right). Both builders' `arc()`, verbatim: the y term carries the
 * aspect because the radius is a fraction of the width, so the arc is a true circle in
 * pixels.
 */
export function arc(deg: number, k: number): [number, number] {
  const a = (deg * Math.PI) / 180;
  return [CX + R * k * Math.cos(a), CY + R * k * Math.sin(a) * PLATE_ASPECT];
}

/** The betting circles' radius on the arc, and the circle's own radius (44 px on the mock's 1100 plate). */
export const SPOT_K = 0.66;
export const CIRCLE_R = 44 / 1100;
/** The spots are 32 degrees apart: build-topdown.py `SPOTS = [42, 74, 106, 138]`. */
export const SPOT_STEP = 32;
/**
 * …across the mock's span, 42 to 138 degrees (96). PHASE 8: five spots at the mock's own
 * step would run to 26 and 154, up the sides of the moon and under the shoe and the
 * tray; so the step is the mock's 32 until the span is full, and five seats share the
 * span at 24. Three seats are the mock's step exactly.
 */
export const SPOT_SPAN = 96;
export const stepFor = (seats: number): number => (seats > 1 ? Math.min(SPOT_STEP, SPOT_SPAN / (seats - 1)) : SPOT_STEP);

/**
 * PHASE 8: the rail's outer edge, as a multiple of the felt radius.
 *
 * Measured, not in either builder: the plate's silhouette bottoms out at 0.998 of its
 * height at the centre column, which is `arc(90, 1.123)` to three decimals, and the same
 * radius reproduces the silhouette at x 0.30 (0.921 against 0.923 measured). A seat
 * sits OUTSIDE the rail — the spec's "avatar outside the rail" — so this is where its
 * portrait's inner edge goes.
 */
export const RAIL_K = 1.123;

/**
 * Which spot each seat sits at, in the engine's seat order.
 *
 * The engine seats the hero at 0 and deals and acts 0, 1, 2… The hero is at the
 * centre-bottom (the brief), and the rest go CLOCKWISE AS SEEN FROM ABOVE from there —
 * the same rule Phase 5 made for poker (0(a)). With the dealer at the top, clockwise
 * along the near arc runs right to left on the screen, i.e. increasing `deg`; past the
 * last spot on the left it comes round to the first on the right, the way a ring does.
 *
 * The spread is the mock's: 32 degrees a spot. The mock drew four spots with the hero
 * third from the right (106); the engine's model is one, three or five with the hero
 * in the middle, so the same step is centred on 90.
 */
export function spotAngles(seats: number): number[] {
  if (![1, 3, 5].includes(seats)) throw new Error(`the half-moon seats 1, 3 or 5 spots, not ${seats}`);
  const side = (seats - 1) / 2;
  const step = stepFor(seats);
  /* Left of the hero first (clockwise), then round to the right end and back in. */
  const left = Array.from({ length: side }, (_, i) => 90 + step * (i + 1));
  const right = Array.from({ length: side }, (_, i) => 90 - step * (side - i));
  return [90, ...left, ...right];
}

/** Every angle in screen order, left to right — for the print and the tests. */
export function spotsLeftToRight(seats: number): number[] {
  return [...spotAngles(seats)].sort((a, b) => b - a);
}

/* ------------------------------------------------------------ the dealer's side --- */

/** Where the dealer stands: the middle of the flat edge. build-blackjack.py `project(0.5, 0.0)`. */
export const DEALER_AT: readonly [number, number] = [0.5, 0];
/** The dealer's hand, just below the tray. build-blackjack.py `project(0.5, 0.215)`. */
export const DEALER_HAND: readonly [number, number] = [0.5, 0.215];
/** The shoe, at the dealer's right. build-blackjack.py `project(0.78, 0.21)`. */
export const SHOE_AT: readonly [number, number] = [0.76, 0.21];
/**
 * PHASE 8: the discard tray, mirrored across the dealer from the shoe. Neither mock drew
 * one (the room reshuffles every hand, so nothing piles up); the brief asks for the slot.
 * It holds a hand's cards between the collect and the next shuffle and then empties
 * back into the shoe — the reshuffle made visible.
 */
export const TRAY_AT: readonly [number, number] = [0.24, 0.21];
/*
 * PHASE 8: the shoe comes in from the mock's 0.78 to 0.76 (and the tray mirrors it at
 * 0.24): at 0.78 the portrait zoom — which shows 0.177 to 0.823 of the plate — cut the
 * end off both slots.
 */
/** The two slots' size, as fractions of the plate WIDTH: build-blackjack.py `.shoe` 120 x 60 on its 1150 plane. */
export const SLOT = { w: 120 / 1150, h: 60 / 1150 } as const;
/** The house's chips, in the brass tray the plate art carries at the centre of its flat edge. */
export const HOUSE_AT: readonly [number, number] = [0.5, 0.07];

/* ------------------------------------------------------------------- the print --- */

/**
 * The printed legends, on two arcs concentric with the felt: build-topdown.py
 * `arcpath(0.31, 152, 28, 'ins')` and `arcpath(0.42, 152, 28, 'pay')`, and the font
 * sizes it set them at (20 and 29 on its 1100 plate).
 */
export const PRINT = {
  insuranceK: 0.25, paysK: 0.32, from: 166, to: 14,
  insuranceSize: 15 / 1100, paysSize: 22 / 1100,
  /** The dealer's rule, small, on an arc under the middle circles, where nothing stands. */
  ruleK: 0.91, ruleFrom: 122, ruleTo: 58, ruleSize: 11 / 1100,
} as const;
/*
 * PHASE 8: the mock's arcs were 0.31 and 0.42 at 20 and 29 px, over 152 to 28 degrees.
 * At those radii the legends run under the cards of every seat that is not in the
 * middle — they do in the mock's own desktop frame (`td03-bj-desktop.jpg`, the Card
 * Sharp's king over "PAYS"). Brought in to 0.25 and 0.32 at three quarters of the size
 * and run over 166 to 14 so the words fit the shorter arcs, both clear the dealer's
 * hand above and every seat's cards below, at every seat count. The dealer's standing
 * rule — which the old felt printed and neither mock did — goes on a third, small arc
 * near the rail, under the middle circles.
 */

/**
 * PHASE 8: where an insurance bet lands — ON the insurance line, in front of its own
 * seat, the way a real layout takes it and the old felt drew it (`FeltPrint`'s line).
 * The mocks never drew one taken. 0.29 is between the two legends; the chips are only
 * ever there while the question is open, when the dealer has two cards and nobody has
 * drawn, so nothing else is in that band.
 */
export const INSURANCE_K = 0.29;
export function insuranceAt(deg: number): [number, number] {
  return arc(deg, INSURANCE_K);
}

/** An SVG arc path along the felt at `k`, from `a0` to `a1` degrees, in plate pixels. */
export function arcPath(k: number, a0: number, a1: number, w: number): string {
  const h = w / PLATE_ASPECT;
  const [x0, y0] = arc(a0, k);
  const [x1, y1] = arc(a1, k);
  const r = R * k * w;
  return `M${(x0 * w).toFixed(1)},${(y0 * h).toFixed(1)} A${r.toFixed(1)},${r.toFixed(1)} 0 0 0 ${(x1 * w).toFixed(1)},${(y1 * h).toFixed(1)}`;
}

/* ----------------------------------------------------------------- a seat's parts --- */

export interface SeatGeometry {
  seat: number;
  deg: number;
  /** The betting circle's centre. */
  spot: [number, number];
  /** Where an insurance bet sits. */
  insurance: [number, number];
  /**
   * Where the seat's portrait goes, in each orientation, and the unit vector the CSS
   * pushes it along by half a portrait so its INNER edge lands on the rail's outer edge.
   */
  land: { at: [number, number]; ux: number; uy: number };
  port: { at: [number, number]; ux: number; uy: number };
}

/**
 * The mock's portrait rule, with one number changed.
 *
 * build-topdown.py: in portrait — and for the seats near the middle in landscape — a
 * seat's plate hangs BELOW the rail at its own spot's x, clamped to [0.14, 0.86]; the
 * rest sit on the arc outside the rail. The mock put that point `0.10` of the plate
 * below the felt's arc; PHASE 8 measures from the rail instead (`RAIL_K`), so the
 * portrait lands on the wood's outer edge at every x rather than on the felt's.
 */
const PORT_CLAMP = [0.14, 0.86] as const;
/**
 * PHASE 8: in portrait a seat's plate hangs at its spot's x pushed out from the middle by
 * this much. The mock's four spots were 32 degrees apart and their plates had 92 px each
 * on a phone; five share the span at 24 and would have 58 — less than a plate is wide.
 * Spread by 1.18 they have 69, and the outermost is still wholly on the screen.
 */
export const PORT_SPREAD = 1.18;

function below(x: number): [number, number] {
  const dx = Math.min(1, Math.abs(x - CX) / (R * RAIL_K));
  return [x, CY + R * RAIL_K * Math.sqrt(1 - dx * dx) * PLATE_ASPECT];
}

export function seatGeometry(seats: number): SeatGeometry[] {
  return spotAngles(seats).map((deg, seat) => {
    const spot = arc(deg, SPOT_K);
    const a = (deg * Math.PI) / 180;
    const outward = { ux: Math.cos(a), uy: Math.sin(a) };
    /*
     * Landscape: on the arc outside the rail, except the middle, where the seat hangs
     * straight below it. build-topdown.py hung everything between 60 and 120 degrees
     * below; PHASE 8 hangs only the player's there — at five seats the neighbours at 66
     * and 114 hung below as well put three plates in 23% of a phone's width.
     */
    const mid = Math.abs(deg - 90) < 12;
    const land = mid
      ? { at: below(spot[0]), ux: 0, uy: 1 }
      : { at: arc(deg, RAIL_K), ...outward };
    const px = Math.min(PORT_CLAMP[1], Math.max(PORT_CLAMP[0], CX + (spot[0] - CX) * PORT_SPREAD));
    const port = { at: below(px), ux: 0, uy: 1 };
    return { seat, deg, spot, insurance: insuranceAt(deg), land, port };
  });
}

/** The YOU disc: beside the hero's circle, on the side away from the next seat. build-topdown.py `yx - 0.075`. */
export const YOU_OFFSET = 0.075;
/**
 * PHASE 8: in front of the player's circle rather than beside it. Beside it (the mock's
 * `- 0.075`) is inside the next circle once five seats share the span 24 degrees apart;
 * in front — between the circle and the rail, where the player sits — nothing else is.
 */
export const YOU_K = 0.79;
export function youDisc(): [number, number] {
  return arc(90, YOU_K);
}

/* ------------------------------------------------------------------ the fit --- */

/**
 * The portrait zoom: build-topdown.py `if H > W: w = W * 1.55` — the plate is drawn at
 * 155% of the stage's width, so the arc's ends run off both edges and the spots, the
 * dealer's side and the print stay in view at half as big again.
 *
 * "The seats the hero can see": at five seats the outermost spots are at 26 and 154
 * degrees, x 0.245 and 0.755 of the plate, and at 155% the stage shows 0.177 to 0.823
 * — every circle, card and plate is inside it (the tests hold that). So the mock's one
 * number serves every seat count and no second zoom exists.
 */
export const PORTRAIT_ZOOM = 1.55;
/** The plate's top edge, as a fraction of the stage's height under the bar. build-topdown.py `top = h/2 + 0.13 H`. */
export const PORTRAIT_TOP = 0.13;

/** The visible slice of the plate, as fractions of its width, at a zoom. */
export function visibleSpan(zoom = PORTRAIT_ZOOM): [number, number] {
  return [0.5 - 0.5 / zoom, 0.5 + 0.5 / zoom];
}

/**
 * Landscape and desktop: the whole moon fits. build-topdown.py `pad = {x: .70, y: .72}`
 * (a phone on its side: `{x: .60, y: .70}`), the plate centred at 46% of the stage.
 */
export const LANDSCAPE_PAD = { x: 0.7, y: 0.72 } as const;
export const SHORT_PAD = { x: 0.6, y: 0.7 } as const;

/**
 * The stagger of a hand of `n` cards, as a fraction of a card: `.62` until the row would
 * pass 2.4 cards wide, then closer — `blackjack.css` `.bk-cardslot + .bk-cardslot`,
 * `min(.62, 1.4 / (n - 1))`. Here, with no DOM, so the flight planner and the tests
 * read one number (PHASE 9 review: the flight aimed the fourth and fifth cards at .62).
 */
export const CARD_STEP_X = 0.62;
export const cardStepFor = (n: number): number => Math.min(CARD_STEP_X, 1.4 / Math.max(1, n - 1));
